import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Beaker,
  Plus,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  Trash2,
  RotateCcw,
  X,
  Calendar,
  User,
  Package,
  Truck,
  FileText,
  History
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SupplierSamples() {
  const { user, loading: authLoading } = useAuth();
  const { companyId, selectedCompany } = useCompany();
  
  const [samples, setSamples] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [sampleHistory, setSampleHistory] = useState([]);
  const [selectedSample, setSelectedSample] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Check if user has edit/delete permission
  const canEdit = user?.effective_permissions?.includes('edit_supplier_samples') || false;
  
  // Form state for new sample
  const [formData, setFormData] = useState({
    supplier_name: '',
    material_name: '',
    date_received: new Date().toISOString().split('T')[0],
    remarks: ''
  });
  
  // Test result state
  const [testData, setTestData] = useState({
    result: '',
    remarks: ''
  });

  const fetchSamples = useCallback(async () => {
    if (!companyId) return;
    
    try {
      const params = new URLSearchParams({
        company_id: companyId,
        limit: '100'
      });
      
      if (statusFilter && statusFilter !== 'all') {
        params.append('result', statusFilter);
      }
      
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      
      const response = await fetch(`${API_URL}/api/supplier-samples?${params}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSamples(data.samples || []);
        setStats(data.stats || { pending: 0, approved: 0, rejected: 0 });
      }
    } catch (error) {
      console.error('Failed to fetch samples:', error);
      toast.error('Failed to load samples');
    } finally {
      setLoading(false);
    }
  }, [companyId, statusFilter, searchQuery]);

  useEffect(() => {
    if (!authLoading && user && companyId) {
      fetchSamples();
    }
  }, [authLoading, user, companyId, fetchSamples]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.supplier_name.trim() || !formData.material_name.trim() || !formData.date_received) {
      toast.error('Please fill in required fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/supplier-samples`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          company_id: companyId
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Sample ${data.sample_id} created`);
        setShowAddModal(false);
        setFormData({
          supplier_name: '',
          material_name: '',
          date_received: new Date().toISOString().split('T')[0],
          remarks: ''
        });
        fetchSamples();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create sample');
      }
    } catch (error) {
      console.error('Failed to create sample:', error);
      toast.error('Failed to create sample');
    } finally {
      setSubmitting(false);
    }
  };

  const openTestModal = (sample) => {
    setSelectedSample(sample);
    setTestData({
      result: '',
      remarks: sample.remarks || ''
    });
    setShowTestModal(true);
  };

  const handleTestSubmit = async () => {
    if (!testData.result) {
      toast.error('Please select a test result');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/supplier-samples/${selectedSample.sample_id}/test`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(testData)
      });

      if (response.ok) {
        toast.success(`Sample ${testData.result.toLowerCase()}`);
        setShowTestModal(false);
        setSelectedSample(null);
        fetchSamples();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update sample');
      }
    } catch (error) {
      console.error('Failed to update sample:', error);
      toast.error('Failed to update sample');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevert = async (sample) => {
    if (!confirm('Are you sure you want to revert this sample to Pending?')) return;

    try {
      const response = await fetch(`${API_URL}/api/supplier-samples/${sample.sample_id}/revert`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Sample reverted to Pending');
        fetchSamples();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to revert sample');
      }
    } catch (error) {
      console.error('Failed to revert sample:', error);
      toast.error('Failed to revert sample');
    }
  };

  const handleDelete = async (sample) => {
    if (!confirm(`Delete sample ${sample.sample_id}?`)) return;

    try {
      const response = await fetch(`${API_URL}/api/supplier-samples/${sample.sample_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Sample deleted');
        fetchSamples();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete sample');
      }
    } catch (error) {
      console.error('Failed to delete sample:', error);
      toast.error('Failed to delete sample');
    }
  };

  const openDetailsModal = async (sample) => {
    setSelectedSample(sample);
    setShowDetailsModal(true);
    setHistoryLoading(true);
    setSampleHistory([]);
    try {
      const response = await fetch(`${API_URL}/api/supplier-samples/${sample.sample_id}/history`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSampleHistory(data.history || []);
        // Refresh selected sample with fresh data
        if (data.sample) setSelectedSample(data.sample);
      } else {
        toast.error('Failed to load sample history');
      }
    } catch (error) {
      console.error('Failed to fetch sample history:', error);
      toast.error('Failed to load sample history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const getStatusBadge = (result, onClick) => {
    const statusConfig = {
      Pending: { class: 'status-pending-lab', icon: Clock, color: 'text-amber-600' },
      Approved: { class: 'status-approved', icon: CheckCircle, color: 'text-green-600' },
      Rejected: { class: 'status-rejected', icon: XCircle, color: 'text-red-600' }
    };
    
    const config = statusConfig[result] || statusConfig.Pending;
    const Icon = config.icon;
    
    if (onClick) {
      return (
        <button
          type="button"
          onClick={onClick}
          className={`status-badge ${config.class} flex items-center gap-1 cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-slate-300 transition-all`}
          title="Click to view details & history"
        >
          <Icon className={`w-3 h-3 ${config.color}`} />
          {result}
        </button>
      );
    }

    return (
      <span className={`status-badge ${config.class} flex items-center gap-1`}>
        <Icon className={`w-3 h-3 ${config.color}`} />
        {result}
      </span>
    );
  };

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="loading-state">
          <div className="loading-spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-content" data-testid="supplier-samples-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Supplier Sample Testing</h1>
            <p className="text-slate-500 mt-1">Track and test raw material samples from suppliers</p>
          </div>
          <Button
            onClick={() => setShowAddModal(true)}
            className="gap-2"
            data-testid="add-sample-btn"
          >
            <Plus className="w-4 h-4" />
            New Sample
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div 
            className="bg-amber-50 border border-amber-200 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setStatusFilter('Pending')}
            data-testid="pending-samples-stat"
          >
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-amber-600" />
              <span className="text-sm font-medium text-amber-800">Pending</span>
            </div>
            <div className="text-2xl font-bold text-amber-900 mt-2">{stats.pending}</div>
          </div>
          
          <div 
            className="bg-green-50 border border-green-200 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setStatusFilter('Approved')}
            data-testid="approved-samples-stat"
          >
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-green-800">Approved</span>
            </div>
            <div className="text-2xl font-bold text-green-900 mt-2">{stats.approved}</div>
          </div>
          
          <div 
            className="bg-red-50 border border-red-200 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setStatusFilter('Rejected')}
            data-testid="rejected-samples-stat"
          >
            <div className="flex items-center gap-3">
              <XCircle className="w-5 h-5 text-red-600" />
              <span className="text-sm font-medium text-red-800">Rejected</span>
            </div>
            <div className="text-2xl font-bold text-red-900 mt-2">{stats.rejected}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="filters-bar">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search supplier or material..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
          </div>
          <div className="w-48">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Pending">Pending</SelectItem>
                <SelectItem value="Approved">Approved</SelectItem>
                <SelectItem value="Rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {statusFilter !== 'all' && (
            <Button variant="ghost" size="sm" onClick={() => setStatusFilter('all')}>
              Clear Filter
            </Button>
          )}
        </div>

        {/* Samples Table */}
        {samples.length === 0 ? (
          <div className="empty-state">
            <Beaker className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-700">No samples found</h3>
            <p className="text-slate-500 mt-1">
              {searchQuery || statusFilter !== 'all' 
                ? 'Try adjusting your filters' 
                : 'Add your first supplier sample to get started'}
            </p>
          </div>
        ) : (
          <div className="table-container">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="table-header-row">
                    <th className="table-header-cell">Sample ID</th>
                    <th className="table-header-cell">Supplier</th>
                    <th className="table-header-cell">Material</th>
                    <th className="table-header-cell">Date Received</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Tested By</th>
                    <th className="table-header-cell">Test Date</th>
                    <th className="table-header-cell">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {samples.map((sample) => (
                    <tr key={sample.sample_id} className="table-row" data-testid={`sample-row-${sample.sample_id}`}>
                      <td className="table-cell font-mono text-sm font-semibold">
                        {sample.sample_id}
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center gap-2">
                          <Truck className="w-4 h-4 text-slate-400" />
                          {sample.supplier_name}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-slate-400" />
                          {sample.material_name}
                        </div>
                      </td>
                      <td className="table-cell">
                        {new Date(sample.date_received).toLocaleDateString()}
                      </td>
                      <td className="table-cell">
                        {getStatusBadge(sample.result, () => openDetailsModal(sample))}
                      </td>
                      <td className="table-cell">
                        {sample.tested_by_name ? (
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-400" />
                            {sample.tested_by_name}
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="table-cell">
                        {sample.test_date ? new Date(sample.test_date).toLocaleDateString() : '-'}
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center gap-2">
                          {sample.result === 'Pending' ? (
                            <Button
                              size="sm"
                              onClick={() => openTestModal(sample)}
                              className="gap-1"
                              data-testid={`test-btn-${sample.sample_id}`}
                            >
                              <Beaker className="w-3 h-3" />
                              Test
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRevert(sample)}
                              className="gap-1"
                              data-testid={`revert-btn-${sample.sample_id}`}
                            >
                              <RotateCcw className="w-3 h-3" />
                              Revert
                            </Button>
                          )}
                          {canEdit && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(sample)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-btn-${sample.sample_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Mobile Card View */}
        <div className="md:hidden space-y-4 mt-4">
          {samples.map((sample) => (
            <div key={sample.sample_id} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`sample-card-${sample.sample_id}`}>
              <div className="flex justify-between items-start mb-3">
                <span className="font-mono text-sm font-semibold">{sample.sample_id}</span>
                {getStatusBadge(sample.result, () => openDetailsModal(sample))}
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Supplier:</span>
                  <span className="font-medium">{sample.supplier_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Material:</span>
                  <span className="font-medium">{sample.material_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Received:</span>
                  <span>{new Date(sample.date_received).toLocaleDateString()}</span>
                </div>
                {sample.tested_by_name && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Tested By:</span>
                    <span>{sample.tested_by_name}</span>
                  </div>
                )}
              </div>
              <div className="flex gap-2 mt-4">
                {sample.result === 'Pending' ? (
                  <Button size="sm" onClick={() => openTestModal(sample)} className="flex-1 gap-1">
                    <Beaker className="w-3 h-3" />
                    Test Sample
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => handleRevert(sample)} className="flex-1 gap-1">
                    <RotateCcw className="w-3 h-3" />
                    Revert
                  </Button>
                )}
                {canEdit && (
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(sample)} className="text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Add Sample Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-md shadow-xl" data-testid="add-sample-modal">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-semibold">Add New Sample</h2>
                <button onClick={() => setShowAddModal(false)} className="p-1 hover:bg-slate-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Supplier Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Truck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      name="supplier_name"
                      value={formData.supplier_name}
                      onChange={handleInputChange}
                      placeholder="Enter supplier name"
                      className="pl-10"
                      required
                      data-testid="supplier-name-input"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Material Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      name="material_name"
                      value={formData.material_name}
                      onChange={handleInputChange}
                      placeholder="Enter material name"
                      className="pl-10"
                      required
                      data-testid="material-name-input"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Date Received <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="date"
                      name="date_received"
                      value={formData.date_received}
                      onChange={handleInputChange}
                      className="pl-10"
                      required
                      data-testid="date-received-input"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Remarks
                  </label>
                  <textarea
                    name="remarks"
                    value={formData.remarks}
                    onChange={handleInputChange}
                    placeholder="Any initial notes..."
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                    rows={3}
                    data-testid="remarks-input"
                  />
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowAddModal(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting} className="flex-1 gap-2" data-testid="submit-sample-btn">
                    {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                    Add Sample
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Test Result Modal */}
        {showTestModal && selectedSample && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-md shadow-xl" data-testid="test-sample-modal">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-semibold">Test Sample: {selectedSample.sample_id}</h2>
                <button onClick={() => setShowTestModal(false)} className="p-1 hover:bg-slate-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-4">
                <div className="bg-slate-50 rounded-lg p-3 mb-4">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-slate-500">Supplier:</span>
                      <span className="ml-2 font-medium">{selectedSample.supplier_name}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Material:</span>
                      <span className="ml-2 font-medium">{selectedSample.material_name}</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Test Result <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setTestData(prev => ({ ...prev, result: 'Approved' }))}
                        className={`p-4 border-2 rounded-lg flex flex-col items-center gap-2 transition-all ${
                          testData.result === 'Approved' 
                            ? 'border-green-500 bg-green-50' 
                            : 'border-slate-200 hover:border-green-300'
                        }`}
                        data-testid="approve-btn"
                      >
                        <CheckCircle className={`w-8 h-8 ${testData.result === 'Approved' ? 'text-green-600' : 'text-slate-400'}`} />
                        <span className={`font-medium ${testData.result === 'Approved' ? 'text-green-700' : 'text-slate-600'}`}>
                          Approve
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setTestData(prev => ({ ...prev, result: 'Rejected' }))}
                        className={`p-4 border-2 rounded-lg flex flex-col items-center gap-2 transition-all ${
                          testData.result === 'Rejected' 
                            ? 'border-red-500 bg-red-50' 
                            : 'border-slate-200 hover:border-red-300'
                        }`}
                        data-testid="reject-btn"
                      >
                        <XCircle className={`w-8 h-8 ${testData.result === 'Rejected' ? 'text-red-600' : 'text-slate-400'}`} />
                        <span className={`font-medium ${testData.result === 'Rejected' ? 'text-red-700' : 'text-slate-600'}`}>
                          Reject
                        </span>
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Test Remarks
                    </label>
                    <textarea
                      value={testData.remarks}
                      onChange={(e) => setTestData(prev => ({ ...prev, remarks: e.target.value }))}
                      placeholder="Add test observations or notes..."
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                      rows={3}
                      data-testid="test-remarks-input"
                    />
                  </div>
                  
                  <div className="flex gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={() => setShowTestModal(false)} className="flex-1">
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleTestSubmit} 
                      disabled={submitting || !testData.result} 
                      className="flex-1 gap-2"
                      data-testid="submit-test-btn"
                    >
                      {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                      Submit Result
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Details & History Modal */}
        {showDetailsModal && selectedSample && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="sample-details-modal">
              <div className="flex items-center justify-between p-4 border-b">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-slate-600" />
                  <div>
                    <h2 className="text-lg font-semibold">Sample Details</h2>
                    <p className="text-xs text-slate-500 font-mono">{selectedSample.sample_id}</p>
                  </div>
                </div>
                <button 
                  onClick={() => { setShowDetailsModal(false); setSelectedSample(null); setSampleHistory([]); }} 
                  className="p-1 hover:bg-slate-100 rounded"
                  data-testid="close-details-modal-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="overflow-y-auto p-4 space-y-4">
                {/* Current Status Card */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-slate-700">Current Status</span>
                    {getStatusBadge(selectedSample.result)}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-slate-500 block text-xs">Supplier</span>
                      <span className="font-medium">{selectedSample.supplier_name}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block text-xs">Material</span>
                      <span className="font-medium">{selectedSample.material_name}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block text-xs">Date Received</span>
                      <span className="font-medium">{new Date(selectedSample.date_received).toLocaleDateString()}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block text-xs">Created By</span>
                      <span className="font-medium">{selectedSample.created_by_name || '-'}</span>
                    </div>
                    {selectedSample.tested_by_name && (
                      <div>
                        <span className="text-slate-500 block text-xs">Tested By</span>
                        <span className="font-medium">{selectedSample.tested_by_name}</span>
                      </div>
                    )}
                    {selectedSample.test_date && (
                      <div>
                        <span className="text-slate-500 block text-xs">Test Date</span>
                        <span className="font-medium">{new Date(selectedSample.test_date).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                  {selectedSample.remarks && (
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <span className="text-slate-500 block text-xs mb-1">Latest Remarks</span>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="sample-latest-remarks">
                        {selectedSample.remarks}
                      </p>
                    </div>
                  )}
                </div>

                {/* Activity History */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <History className="w-4 h-4 text-slate-600" />
                    <h3 className="text-sm font-semibold text-slate-700">Activity Log</h3>
                  </div>
                  
                  {historyLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                    </div>
                  ) : sampleHistory.length === 0 ? (
                    <div className="text-center py-6 text-sm text-slate-500 bg-slate-50 rounded-lg">
                      No activity records found
                    </div>
                  ) : (
                    <div className="space-y-3" data-testid="sample-history-list">
                      {sampleHistory.map((log, idx) => {
                        const actionLabels = {
                          supplier_sample_created: { label: 'Sample Created', icon: Plus, color: 'text-slate-600', bg: 'bg-slate-100' },
                          supplier_sample_approved: { label: 'Approved', icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
                          supplier_sample_rejected: { label: 'Rejected', icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
                          supplier_sample_reverted: { label: 'Reverted to Pending', icon: RotateCcw, color: 'text-amber-600', bg: 'bg-amber-50' },
                          supplier_sample_deleted: { label: 'Deleted', icon: Trash2, color: 'text-red-600', bg: 'bg-red-50' }
                        };
                        const meta = actionLabels[log.action] || { label: log.action, icon: FileText, color: 'text-slate-600', bg: 'bg-slate-50' };
                        const Icon = meta.icon;
                        const logRemarks = log.details?.remarks;
                        
                        return (
                          <div 
                            key={log.activity_id || idx} 
                            className={`rounded-lg border border-slate-200 p-3 ${meta.bg}`}
                            data-testid={`history-log-${idx}`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`mt-0.5 ${meta.color}`}>
                                <Icon className="w-4 h-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                  <span className={`text-sm font-semibold ${meta.color}`}>{meta.label}</span>
                                  <span className="text-xs text-slate-500">
                                    {new Date(log.timestamp).toLocaleString()}
                                  </span>
                                </div>
                                <div className="text-xs text-slate-600 mt-1 flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  <span>{log.user_name}</span>
                                  {log.user_role && <span className="text-slate-400">· {log.user_role}</span>}
                                </div>
                                {logRemarks && (
                                  <div className="mt-2 p-2 bg-white/60 rounded border border-slate-200">
                                    <p className="text-xs text-slate-500 mb-1">Remarks:</p>
                                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{logRemarks}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t p-4 flex justify-end">
                <Button 
                  variant="outline" 
                  onClick={() => { setShowDetailsModal(false); setSelectedSample(null); setSampleHistory([]); }}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
